/** Eksportene under «festival-operations» i katalogen (docs/EKSPORT.md §9). API-halvdelen: `apps/api/src/export/definitions/festival-operations/`. */
import type { ExportColumnMeta, ExportMeta } from '../types.js';

const col = (
  group: string,
  key: string,
  type: ExportColumnMeta['type'],
  extra: Omit<ExportColumnMeta, 'key' | 'labelKey' | 'type'> = {},
): ExportColumnMeta => ({
  key,
  labelKey: `export.festivalOperations.${group}.columns.${key}`,
  type,
  ...extra,
});

/**
 * RESSURSER: lista på Ressurser med samme filter (avdelingen i treet, typen, søket) og de samme
 * kolonnene (`tableKey: 'edition-resources'`). `parent` er hele årgangen.
 */
const RESOURCES: ExportMeta = {
  id: 'festival.resources',
  domain: 'festival',
  kind: 'table',
  titleKey: 'export.festivalOperations.resources.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter', 'parent'],
  requires: ['resource:list'],
  /* H3: en gjest med delt avdeling eller ressurs får det delte (RLS), uten kostnad (som skjermen). */
  guest: true,
  tableKey: 'edition-resources',
  columns: [
    col('resources', 'resource', 'text'),
    col('resources', 'type', 'text'),
    col('resources', 'alsoIn', 'text'),
    col('resources', 'owner', 'text'),
    col('resources', 'primaryDepartment', 'text', { defaultHidden: true }),
    col('resources', 'hours', 'text', { defaultHidden: true }),
    col('resources', 'capacity', 'text', { defaultHidden: true }),
    col('resources', 'size', 'text', { defaultHidden: true }),
    col('resources', 'cost', 'money', { defaultHidden: true }),
    col('resources', 'costPer', 'text', { defaultHidden: true }),
    col('resources', 'notes', 'multiline', { defaultHidden: true }),
  ],
  params: ['editionId'],
  landscape: true,
};

/**
 * RESERVASJONER: tildelingene i årgangen, gruppert per ressurs eller per dag (`params.groupBy`,
 * `resource` | `day`). `parent` er hele årgangen, `object` én ressurs, `filter` vinduet og
 * avdelingen Uke/Dag viser (`ListAllocationsQuery` uten side).
 */
const RESERVATIONS: ExportMeta = {
  id: 'festival.reservations',
  domain: 'festival',
  kind: 'table',
  titleKey: 'export.festivalOperations.reservations.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object', 'filter', 'parent'],
  requires: ['allocation:list'],
  /* H3: reservasjonene på det som er delt med gjesten, og ingenting annet (RLS). */
  guest: true,
  columns: [
    col('reservations', 'resource', 'text'),
    col('reservations', 'type', 'text', { defaultHidden: true }),
    col('reservations', 'date', 'date'),
    col('reservations', 'from', 'time'),
    col('reservations', 'to', 'time'),
    col('reservations', 'toDate', 'date', { defaultHidden: true }),
    col('reservations', 'title', 'text'),
    col('reservations', 'source', 'text'),
    col('reservations', 'quantity', 'integer'),
    col('reservations', 'people', 'multiline'),
    col('reservations', 'department', 'text', { defaultHidden: true }),
    col('reservations', 'status', 'status'),
    col('reservations', 'note', 'multiline', { defaultHidden: true }),
  ],
  params: ['editionId'],
  landscape: true,
};

/**
 * TIDSLINJE FOR <RESSURS> (QA13 punkt 81): det ressurspanelets tidslinje viser — konsertene,
 * scenetidene, vaktene, reservasjonene og åpningstida — fra hendelseslaget (C1). `object` er
 * ressursen; `params.layers` er lagene som er valgt på skjermen.
 */
const RESOURCE_TIMELINE: ExportMeta = {
  id: 'festival.resource-timeline',
  domain: 'festival',
  kind: 'table',
  titleKey: 'export.festivalOperations.resourceTimeline.name',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['object'],
  requires: ['allocation:list'],
  /* Som reservasjonene: gjesten får det RLS gir på det som er delt. */
  guest: true,
  columns: [
    col('resourceTimeline', 'date', 'date'),
    col('resourceTimeline', 'from', 'time'),
    col('resourceTimeline', 'to', 'time'),
    col('resourceTimeline', 'layer', 'text'),
    col('resourceTimeline', 'title', 'text'),
    col('resourceTimeline', 'detail', 'text'),
    col('resourceTimeline', 'people', 'multiline'),
  ],
  params: ['editionId'],
  landscape: true,
};

/**
 * OPPGAVER: lista på Oppgaver — organisasjonens (`/tasks`) og årgangens — med samme filter og
 * sortering (`ListTasksQuery` uten side), og de merkede. Perioden er fristvinduet (`dueFrom`/`dueTo`)
 * i filteret, eller `params.from`/`params.to` når eksporten startes fra kalenderen.
 */
const TASKS: ExportMeta = {
  id: 'common.tasks',
  domain: 'common',
  kind: 'table',
  titleKey: 'export.festivalOperations.tasks.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['selection', 'filter'],
  requires: ['task:list'],
  guest: false,
  tableKey: 'tasks',
  columns: [
    col('tasks', 'title', 'text'),
    col('tasks', 'assignee', 'text'),
    col('tasks', 'dueAt', 'date'),
    col('tasks', 'status', 'status'),
    col('tasks', 'priority', 'status'),
    col('tasks', 'department', 'text'),
    col('tasks', 'edition', 'text'),
    col('tasks', 'description', 'multiline', { defaultHidden: true }),
    col('tasks', 'startsAt', 'date', { defaultHidden: true }),
    col('tasks', 'estimateMinutes', 'integer', { defaultHidden: true }),
    col('tasks', 'labels', 'text', { defaultHidden: true }),
    col('tasks', 'checklist', 'text', { defaultHidden: true }),
    col('tasks', 'comments', 'integer', { defaultHidden: true }),
    col('tasks', 'attachments', 'integer', { defaultHidden: true }),
    col('tasks', 'performance', 'text', { defaultHidden: true }),
    col('tasks', 'creator', 'text', { defaultHidden: true }),
    col('tasks', 'createdAt', 'date', { defaultHidden: true }),
    col('tasks', 'updatedAt', 'date', { defaultHidden: true }),
    col('tasks', 'completedAt', 'date', { defaultHidden: true }),
  ],
  landscape: true,
};

/**
 * KALENDER FOR EN PERIODE: elementene kalenderen viser i vinduet (`CalendarItemsQuery`: `from`/`to`,
 * årgangen, typene, avdelingen, personen, søket og «Min kalender»), én rad per element, per dag.
 */
const CALENDAR: ExportMeta = {
  id: 'common.calendar',
  domain: 'common',
  kind: 'table',
  titleKey: 'export.festivalOperations.calendar.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['filter'],
  requires: ['event:list'],
  guest: false,
  columns: [
    col('calendar', 'date', 'date'),
    col('calendar', 'from', 'time'),
    col('calendar', 'to', 'time'),
    col('calendar', 'title', 'text'),
    col('calendar', 'kind', 'status'),
    col('calendar', 'details', 'text'),
    col('calendar', 'status', 'status', { defaultHidden: true }),
    col('calendar', 'people', 'integer', { defaultHidden: true }),
    col('calendar', 'toDate', 'date', { defaultHidden: true }),
  ],
};

/** KOSTNADSLINJER: linjene under Oppsummering › Økonomi, i skjermens rekkefølge, med summer. */
const COST_LINES: ExportMeta = {
  id: 'festival.cost-lines',
  domain: 'festival',
  kind: 'table',
  titleKey: 'export.festivalOperations.costLines.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['parent'],
  requires: ['economy:read'],
  guest: false,
  columns: [
    col('costLines', 'incurredOn', 'date'),
    col('costLines', 'category', 'text'),
    col('costLines', 'description', 'text'),
    col('costLines', 'performance', 'text'),
    col('costLines', 'kind', 'status'),
    col('costLines', 'budget', 'money'),
    col('costLines', 'actual', 'money'),
    col('costLines', 'rebill', 'boolean', { defaultHidden: true }),
    col('costLines', 'artistFee', 'boolean', { defaultHidden: true }),
  ],
  params: ['editionId'],
  landscape: true,
};

/** BUDSJETT PER KATEGORI: «Forbruk mot budsjett» — budsjett, faktisk og igjen, med summer. */
const BUDGET: ExportMeta = {
  id: 'festival.budget',
  domain: 'festival',
  kind: 'table',
  titleKey: 'export.festivalOperations.budget.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['parent'],
  requires: ['economy:read'],
  guest: false,
  columns: [
    col('budget', 'category', 'text'),
    col('budget', 'budget', 'money'),
    col('budget', 'actual', 'money'),
    col('budget', 'remaining', 'money'),
    col('budget', 'lines', 'integer', { defaultHidden: true }),
  ],
  params: ['editionId'],
};

/**
 * HONORAROVERSIKT: per programpost, honorarlinjene i økonomien (`artistFee`) og kontraktens honorar.
 * Kontraktens tall bare med `contract:read`, som fanen.
 */
const FEES: ExportMeta = {
  id: 'festival.fees',
  domain: 'festival',
  kind: 'table',
  titleKey: 'export.festivalOperations.fees.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['parent', 'selection'],
  requires: ['economy:read', 'performance:list'],
  guest: false,
  columns: [
    col('fees', 'performance', 'text'),
    col('fees', 'date', 'date'),
    col('fees', 'status', 'status'),
    col('fees', 'contractFee', 'money', { requires: ['contract:read'] }),
    col('fees', 'feeKind', 'text', { requires: ['contract:read'], defaultHidden: true }),
    col('fees', 'budget', 'money'),
    col('fees', 'actual', 'money'),
    col('fees', 'posting', 'status', { requires: ['contract:read'] }),
    col('fees', 'stage', 'text', { defaultHidden: true }),
  ],
  params: ['editionId'],
  landscape: true,
};

/**
 * KONTRAKTOVERSIKT: én rad per programpost med status, honorar, faktura og de signerte versjonene.
 * Selve kontraktfilene lastes ned på kontraktfanen som før.
 */
const CONTRACTS: ExportMeta = {
  id: 'festival.contracts',
  domain: 'festival',
  kind: 'table',
  titleKey: 'export.festivalOperations.contracts.title',
  formats: ['pdf', 'docx', 'csv'],
  scopes: ['parent', 'selection'],
  requires: ['contract:read', 'performance:list'],
  guest: false,
  columns: [
    col('contracts', 'performance', 'text'),
    col('contracts', 'artist', 'text', { defaultHidden: true }),
    col('contracts', 'date', 'date'),
    col('contracts', 'status', 'status'),
    col('contracts', 'signed', 'date'),
    col('contracts', 'fee', 'money'),
    col('contracts', 'feeKind', 'text', { defaultHidden: true }),
    col('contracts', 'invoiceStatus', 'status'),
    col('contracts', 'invoiceReference', 'text', { defaultHidden: true }),
    col('contracts', 'documents', 'multiline'),
    col('contracts', 'stage', 'text', { defaultHidden: true }),
  ],
  params: ['editionId'],
  landscape: true,
};

export const FESTIVAL_OPERATIONS_EXPORTS: readonly ExportMeta[] = [
  RESOURCES,
  RESERVATIONS,
  RESOURCE_TIMELINE,
  TASKS,
  CALENDAR,
  COST_LINES,
  BUDGET,
  FEES,
  CONTRACTS,
];
